// API reliability utilities: retry with backoff and request timeout

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  shouldRetry?: (error: unknown) => boolean;
}

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  shouldRetry: isRetryableError,
};

function isRetryableError(error: unknown): boolean {
  // Network errors (TypeError from fetch)
  if (error instanceof TypeError && error.message.includes('fetch')) return true;

  // HTTP errors stored on our custom error shape
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    // Rate limited
    if (msg.includes('429') || msg.includes('rate limit')) return true;
    // Server errors
    if (msg.includes('500') || msg.includes('502') || msg.includes('503') || msg.includes('504')) return true;
    // Network-level failures
    if (msg.includes('network') || msg.includes('failed to fetch') || msg.includes('econnreset')) return true;
  }

  return false;
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: unknown;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt >= opts.maxRetries || !opts.shouldRetry(error)) {
        throw error;
      }

      // Exponential backoff with jitter
      const delay = Math.min(
        opts.baseDelay * Math.pow(2, attempt) + Math.random() * opts.baseDelay,
        opts.maxDelay
      );

      console.warn(`[api-utils] Retry ${attempt + 1}/${opts.maxRetries} after ${Math.round(delay)}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs = 30000,
  signal?: AbortSignal
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Request timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    // If an external signal is already aborted, reject immediately
    if (signal?.aborted) {
      clearTimeout(timer);
      reject(new Error('Request aborted'));
      return;
    }

    // Listen for external abort signal
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error('Request aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    promise
      .then((result) => {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
        reject(error);
      });
  });
}
